import type { Group, GroupBounds } from '@/@types/group';
import Common from '@/libs/common';

/**
 * Compute the centroid (average lat/lon) of a polygon bounds.
 * Coordinates are in GeoJSON [lon, lat] order.
 */
export function getBoundsCenter(bounds: GroupBounds): { lat: number; lon: number } | null {
  if (!bounds.coordinates || bounds.coordinates.length === 0) return null;
  let sumLon = 0;
  let sumLat = 0;
  for (const [lon, lat] of bounds.coordinates) {
    sumLon += lon;
    sumLat += lat;
  }
  const count = bounds.coordinates.length;
  return { lat: sumLat / count, lon: sumLon / count };
}

type Ring = Array<[number, number]>;

// Cross products of float degrees never cancel exactly on an oblique edge.
const EDGE_TOLERANCE = 1e-12;

function getRing(bounds: GroupBounds): Ring | null {
  const ring = bounds.coordinates;
  return ring && ring.length >= 3 ? ring : null;
}

function isOnSegment(
  lon: number,
  lat: number,
  [lonA, latA]: [number, number],
  [lonB, latB]: [number, number],
) {
  const cross = (lon - lonA) * (latB - latA) - (lat - latA) * (lonB - lonA);
  if (Math.abs(cross) > EDGE_TOLERANCE) return false;
  return (
    Math.min(lonA, lonB) <= lon &&
    lon <= Math.max(lonA, lonB) &&
    Math.min(latA, latB) <= lat &&
    lat <= Math.max(latA, latB)
  );
}

/**
 * Ray casting on the `[lon, lat]` ring. A point on an edge counts as inside:
 * standing on a network's limit still offers that network.
 */
export function isPointInBounds(point: { lat: number; lon: number }, bounds: GroupBounds): boolean {
  const ring = getRing(bounds);
  if (!ring) return false;
  let isInside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    if (isOnSegment(point.lon, point.lat, ring[i], ring[j])) return true;
    const [lonI, latI] = ring[i];
    const [lonJ, latJ] = ring[j];
    if (
      latI > point.lat !== latJ > point.lat &&
      point.lon < ((lonJ - lonI) * (point.lat - latI)) / (latJ - latI) + lonI
    ) {
      isInside = !isInside;
    }
  }
  return isInside;
}

/** Shoelace area, in square degrees: only meant to rank overlapping perimeters. */
export function getBoundsArea(bounds: GroupBounds): number {
  const ring = getRing(bounds);
  if (!ring) return 0;
  let twiceArea = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    twiceArea += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1];
  }
  return Math.abs(twiceArea) / 2;
}

/** Networks whose perimeter contains the position, smallest perimeter first. */
export function findGroupsAtPosition(groups: Group[], position: { lat: number; lon: number }): Group[] {
  return groups
    .filter(g => g.bounds && isPointInBounds(position, g.bounds))
    .map(g => ({ group: g, area: getBoundsArea(g.bounds!) }))
    .sort((a, b) => a.area - b.area)
    .map(({ group }) => group);
}

/** Networks by distance from the position to their perimeter's center; those without a center come apart. */
export function rankGroupsByDistance(
  groups: Group[],
  position: { lat: number; lon: number },
): { ranked: Group[]; unranked: Group[] } {
  const distances = new Map<string, number>();
  for (const g of groups) {
    const center = g.bounds && getBoundsCenter(g.bounds);
    if (!center) continue;
    distances.set(
      g.group_id,
      Common.coordDistance(
        { latitude: position.lat, longitude: position.lon },
        { latitude: center.lat, longitude: center.lon },
      ),
    );
  }
  return {
    ranked: groups
      .filter(g => distances.has(g.group_id))
      .sort((a, b) => distances.get(a.group_id)! - distances.get(b.group_id)!),
    unranked: groups.filter(g => !distances.has(g.group_id)),
  };
}
